import { useEffect, useState, lazy, Suspense } from "react";
import { ShieldAlert, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CATEGORIES, SAFEGUARD_NEEDS, CONTACT_METHODS, AGE_GROUPS, LANGUAGES, SPECIAL_GKK } from "@/lib/constants";
import { Field, CheckRow, BigButton, inputCls, textareaCls, GkkOptions } from "@/components/FormBits";

const LocationPicker = lazy(() => import("@/components/LocationMap").then((m) => ({ default: m.LocationPicker })));

const STEPS = ["s_safety", "s_about", "s_needs", "s_details", "s_consent"];

export const initialForm = (type, lang) => ({
  request_type: type, full_name: "", preferred_name: "", age_group: "", contact_number: "", email: "",
  contact_method: "phone_call", contact_time: "", gkk: "", address: "", language: lang || "ceb",
  categories: type === "talk" ? ["A"] : [], needs: type === "talk" ? ["A_counseling"] : [], other_need: "", description: "",
  assistance_received: "", preferred_assistance: "", home_visit_requested: type === "home_visit",
  on_behalf: false, beneficiary_name: "", relationship: "", beneficiary_permission: false, minor_involved: false,
  safety_concern: false, location: null, consent_contact: true, consent_truthful: false, survey_ref: "", website: "", consent_privacy: false, consent_share_minimum: false, consent_followup: false,
});

export const EmergencyBox = () => (
  <div data-testid="emergency-guidance" className="rounded-2xl border-2 border-[#B85028] bg-[#F6E3DC] p-5 text-[#5C1F0B]">
    <p className="font-bold text-lg flex items-center gap-2"><ShieldAlert className="w-6 h-6" /> Please get help right now</p>
    <p className="mt-2">This form is not monitored continuously. If someone is in immediate danger, is seriously hurt, or may harm themselves or others:</p>
    <ul className="mt-2 list-disc pl-5 space-y-1">
      <li>Call <a href="tel:911" className="font-bold underline">911</a> (national emergency hotline) or go to the nearest hospital or police station.</li>
      <li>Stay with the person if it is safe to do so.</li>
      <li>You may still send this request so that the parish can follow up confidentially afterwards.</li>
    </ul>
    <a href="tel:911" data-testid="call-911-button" className="mt-4 inline-flex h-12 px-6 rounded-full bg-[#B85028] text-white font-bold items-center gap-2"><Phone className="w-5 h-5" /> Call 911</a>
  </div>
);

const Stepper = ({ step, t }) => (
  <ol className="flex gap-2 mb-8" aria-label="Progress">
    {STEPS.map((s, i) => (
      <li key={s} data-testid={`form-step-indicator-${i}`} className="flex-1">
        <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#3D6346]" : "bg-[#E7DFD5]"}`} />
        <p className={`mt-2 text-xs hidden sm:block ${i === step ? "font-bold text-[#241D17]" : "text-[#605349]"}`}>{t(s)}</p>
      </li>
    ))}
  </ol>
);

function StepSafety({ f, set, danger, setDanger, t }) {
  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold">{t("danger_q")}</h2>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <BigButton type="button" data-testid="danger-yes-button" variant={danger === true ? "primary" : "ghost"} onClick={() => { setDanger(true); set({ safety_concern: true }); }}>{t("yes")}</BigButton>
        <BigButton type="button" data-testid="danger-no-button" variant={danger === false ? "green" : "ghost"} onClick={() => { setDanger(false); set({ safety_concern: f.needs.some((n) => SAFEGUARD_NEEDS.includes(n)) }); }}>{t("no")}</BigButton>
      </div>
      {danger && <EmergencyBox />}
      <p className="text-sm text-[#605349]">Messages sent here are never sacramental confession. Please do not write anything you wish to confess; a priest will gladly hear your confession in person.</p>
    </div>
  );
}

function StepAbout({ f, set, gkks, t }) {
  const needsContact = f.contact_method !== "parish_office";
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2"><Field label={t("full_name")} required htmlFor="full_name"><input id="full_name" data-testid="input-full-name" className={inputCls} value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} /></Field></div>
      <Field label={t("preferred_name")} htmlFor="pn"><input id="pn" data-testid="input-preferred-name" className={inputCls} value={f.preferred_name} onChange={(e) => set({ preferred_name: e.target.value })} /></Field>
      <Field label={t("age_group")} htmlFor="ag"><select id="ag" data-testid="select-age-group" className={inputCls} value={f.age_group} onChange={(e) => set({ age_group: e.target.value })}><option value="">—</option>{AGE_GROUPS.map((a) => <option key={a}>{a}</option>)}</select></Field>
      <Field label={t("contact_method")} required htmlFor="cm"><select id="cm" data-testid="select-contact-method" className={inputCls} value={f.contact_method} onChange={(e) => set({ contact_method: e.target.value })}>{Object.entries(CONTACT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
      <Field label={t("contact_number")} required={needsContact && !f.email} htmlFor="cn"><input id="cn" data-testid="input-contact-number" inputMode="tel" className={inputCls} value={f.contact_number} onChange={(e) => set({ contact_number: e.target.value })} placeholder="09XX XXX XXXX" /></Field>
      <Field label={t("email")} htmlFor="em"><input id="em" data-testid="input-email" type="email" className={inputCls} value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
      <Field label={t("contact_time")} htmlFor="ct"><input id="ct" data-testid="input-contact-time" className={inputCls} value={f.contact_time} onChange={(e) => set({ contact_time: e.target.value })} placeholder="e.g. weekday mornings" /></Field>
      <Field label={t("gkk")} required htmlFor="gkk" hint="GKKs are grouped by zone. If unsure, choose 'I don't know my GKK'.">
        <select id="gkk" data-testid="select-gkk" className={inputCls} value={f.gkk} onChange={(e) => set({ gkk: e.target.value })}>
          <option value="">Choose…</option>
          <GkkOptions gkks={gkks} />
          <optgroup label="Other">{SPECIAL_GKK.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</optgroup>
        </select>
      </Field>
      <Field label={t("language")} htmlFor="lng"><select id="lng" data-testid="select-language" className={inputCls} value={f.language} onChange={(e) => set({ language: e.target.value })}>{Object.entries(LANGUAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
      <Field label={t("survey_ref")} htmlFor="sr" hint={t("survey_ref_hint")}><input id="sr" data-testid="input-survey-ref" className={inputCls} value={f.survey_ref} onChange={(e) => set({ survey_ref: e.target.value })} /></Field>
      <div className="hidden" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set({ website: e.target.value })} /></div>
      <div className="sm:col-span-2"><Field label={t("address")} required={!f.survey_ref.trim()} htmlFor="addr" hint={f.survey_ref.trim() ? "Optional — the parish office can fill this in from your Parish Survey record." : "Purok, street or landmark is enough."}><input id="addr" data-testid="input-address" className={inputCls} value={f.address} onChange={(e) => set({ address: e.target.value })} /></Field></div>
      <div className="sm:col-span-2"><Field label="Show us where you are on the map (optional)">
        <Suspense fallback={<p className="text-sm text-[#605349]">Loading map…</p>}><LocationPicker value={f.location} onChange={(location) => set({ location })} /></Suspense>
      </Field></div>
    </div>
  );
}

function StepNeeds({ f, set, danger }) {
  const toggle = (cat, need, on) => {
    const needs = on ? [...f.needs, need] : f.needs.filter((n) => n !== need);
    const cats = CATEGORIES.filter((c) => c.needs.some(([k]) => needs.includes(k))).map((c) => c.code);
    set({ needs, categories: cats, safety_concern: danger === true || needs.some((n) => SAFEGUARD_NEEDS.includes(n)) });
  };
  const sg = f.needs.some((n) => SAFEGUARD_NEEDS.includes(n));
  return (
    <div className="space-y-7">
      <p className="text-[#605349]">Choose one or more. You do not need to share private medical details or diagnoses.</p>
      {CATEGORIES.map((c) => (
        <fieldset key={c.code} data-testid={`category-${c.code}`}>
          <legend className="font-serif-head font-bold text-lg text-[#4A2E1A] mb-3">{c.code}. {c.label}</legend>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {c.needs.map(([k, label]) => (
              <CheckRow key={k} testid={`need-${k}`} checked={f.needs.includes(k)} onChange={(on) => toggle(c.code, k, on)} label={label} tone={SAFEGUARD_NEEDS.includes(k) ? "warn" : undefined} />
            ))}
          </div>
          {c.code === "A" && <p className="mt-2 text-sm text-[#605349]">Sacramental requests are passed to an authorized priest. Sacraments are never administered online.</p>}
          {c.code === "B" && <p className="mt-2 text-sm text-[#605349]">The parish helps arrange assistance and referrals; it does not diagnose or prescribe treatment.</p>}
        </fieldset>
      ))}
      {f.needs.includes("G_other") && <Field label="Please describe the other need" htmlFor="on"><input id="on" data-testid="input-other-need" className={inputCls} value={f.other_need} onChange={(e) => set({ other_need: e.target.value })} /></Field>}
      {sg && (
        <div className="space-y-3" data-testid="safeguarding-pathway-notice">
          <div className="rounded-xl bg-[#FDF7E7] border border-[#E9D49A] p-4 text-[15px] text-[#5A4210]">This request will go through a separate <b>confidential safeguarding pathway</b>, seen only by specially designated and trained personnel.</div>
          <EmergencyBox />
        </div>
      )}
    </div>
  );
}

function StepDetails({ f, set, t }) {
  return (
    <div className="space-y-5">
      <Field label={t("description")} required htmlFor="desc" hint={`${f.description.length}/2000 · Share only what you are comfortable sharing.`}>
        <textarea id="desc" data-testid="input-description" maxLength={2000} className={textareaCls} value={f.description} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <Field label="Assistance you have already received (optional)" htmlFor="ar"><input id="ar" data-testid="input-assistance-received" className={inputCls} value={f.assistance_received} onChange={(e) => set({ assistance_received: e.target.value })} /></Field>
      <Field label="What kind of help would you prefer? (optional)" htmlFor="pa"><input id="pa" data-testid="input-preferred-assistance" className={inputCls} value={f.preferred_assistance} onChange={(e) => set({ preferred_assistance: e.target.value })} /></Field>
      <CheckRow testid="check-home-visit" checked={f.home_visit_requested} onChange={(v) => set({ home_visit_requested: v })} label="I would like a home visit" />
      <CheckRow testid="check-minor-involved" checked={f.minor_involved} onChange={(v) => set({ minor_involved: v })} label="This request involves a child (below 18)" sub="Child-safe procedures apply; a parent or guardian will be involved when appropriate and safe." />
      <CheckRow testid="check-on-behalf" checked={f.on_behalf} onChange={(v) => set({ on_behalf: v })} label="I am asking on behalf of another person" />
      {f.on_behalf && (
        <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-[#E7DFD5] bg-[#FAF8F5] p-4">
          <Field label="Name of the person" required htmlFor="bn"><input id="bn" data-testid="input-beneficiary-name" className={inputCls} value={f.beneficiary_name} onChange={(e) => set({ beneficiary_name: e.target.value })} /></Field>
          <Field label="Your relationship to them" required htmlFor="rel"><input id="rel" data-testid="input-relationship" className={inputCls} value={f.relationship} onChange={(e) => set({ relationship: e.target.value })} /></Field>
          <div className="sm:col-span-2"><CheckRow testid="check-beneficiary-permission" checked={f.beneficiary_permission} onChange={(v) => set({ beneficiary_permission: v })} label="The person knows and agrees that I am asking for help for them" sub="The parish will verify this before sharing any private details with you." /></div>
        </div>
      )}
    </div>
  );
}

function StepConsent({ f, set }) {
  return (
    <div className="space-y-3">
      <CheckRow testid="consent-privacy" checked={f.consent_privacy} onChange={(v) => set({ consent_privacy: v })} label="I have read and understood the privacy notice (required)" sub={<a href="/privacy" target="_blank" rel="noreferrer" className="underline">Open privacy notice</a>} />
      <CheckRow testid="consent-truthful" checked={f.consent_truthful} onChange={(v) => set({ consent_truthful: v })} label="I declare that the information I gave is true and that this is a genuine request (required)" sub="Prank, false or deceptive requests take help away from people in real need and may be refused. The office may verify your identity and parishioner status before assistance." />
      <CheckRow testid="consent-contact" checked={f.consent_contact} onChange={(v) => set({ consent_contact: v })} label="The parish may contact me about this request" />
      <CheckRow testid="consent-share-minimum" checked={f.consent_share_minimum} onChange={(v) => set({ consent_share_minimum: v })} label="The parish may share the minimum necessary information with a specifically authorized parish worker or referral partner, if needed" sub="Optional. Without this, the parish will ask you first before any referral." />
      <CheckRow testid="consent-followup" checked={f.consent_followup} onChange={(v) => set({ consent_followup: v })} label="I would like to receive optional follow-up communications" sub="Optional." />
      <p className="text-sm text-[#605349] pt-2">Optional permissions are never a condition for receiving help. Submitting a request does not guarantee immediate approval, a particular kind of assistance, or a specific response time.</p>
    </div>
  );
}

function validate(step, f) {
  if (step === 1) {
    if (!f.full_name.trim()) return "Please enter your full name.";
    if (f.contact_method !== "parish_office" && !f.contact_number.trim() && !f.email.trim()) return "Please give a contact number or email, or choose to visit the parish office.";
    if (f.contact_number.trim() && !/^(09\d{9}|639\d{9}|0?83\d{7}|\d{7,8})$/.test(f.contact_number.replace(/\D/g, ""))) return "Please enter a valid Philippine phone number, e.g. 09171234567.";
    if (!f.gkk) return "Please choose your GKK or one of the other options.";
    if (!f.address.trim() && !f.survey_ref.trim()) return "Please enter your address or location, or your Parish Survey reference number.";
  }
  if (step === 2 && f.categories.length === 0) return "Please choose at least one need.";
  if (step === 3) {
    if (!f.description.trim()) return "Please briefly describe your request.";
    if (f.on_behalf && (!f.beneficiary_name.trim() || !f.relationship.trim())) return "Please give the person's name and your relationship.";
  }
  if (step === 4 && !f.consent_privacy) return "Please acknowledge the privacy notice.";
  if (step === 4 && !f.consent_truthful) return "Please confirm that the information you gave is true.";
  return null;
}

export default function RequestForm({ type, endpoint = "/public/requests", onDone }) {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [danger, setDanger] = useState(null);
  const [f, setF] = useState(() => initialForm(type, lang));
  const [gkks, setGkks] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((o) => ({ ...o, ...p }));

  useEffect(() => { api.get("/public/gkks").then((r) => setGkks(r.data)).catch(() => setGkks([])); }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const next = () => {
    if (step === 0 && danger === null) { toast.error("Please answer the safety question."); return; }
    const err = validate(step, f);
    if (err) { toast.error(err); return; }
    setStep(step + 1);
  };

  const submit = async () => {
    const err = validate(4, f);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
      body.location_lat = f.location?.lat ?? null;
      body.location_lng = f.location?.lng ?? null;
      delete body.location;
      const { data } = await api.post(endpoint, body);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const props = { f, set, t, gkks, danger, setDanger };
  return (
    <div data-testid="request-form">
      <Stepper step={step} t={t} />
      <h2 className="sr-only">{t(STEPS[step])}</h2>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#3D6346] mb-4">Step {step + 1} of 5 · {t(STEPS[step])}</p>
      <div className="fade-up" key={step}>
        {step === 0 && <StepSafety {...props} />}
        {step === 1 && <StepAbout {...props} />}
        {step === 2 && <StepNeeds {...props} />}
        {step === 3 && <StepDetails {...props} />}
        {step === 4 && <StepConsent {...props} />}
      </div>
      <div className="mt-10 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
        {step > 0 ? <BigButton type="button" variant="ghost" data-testid="form-back-button" onClick={() => setStep(step - 1)}>{t("back")}</BigButton> : <span />}
        {step < 4
          ? <BigButton type="button" data-testid="form-next-button" onClick={next}>{t("next")}</BigButton>
          : <BigButton type="button" variant="green" data-testid="submit-request-button" disabled={busy} onClick={submit}><Check className="w-5 h-5" />{busy ? "Sending…" : t("submit")}</BigButton>}
      </div>
    </div>
  );
}
